import {SharableEntityType, UserRole} from 'common/report/util/saved-entities/types';
import {getReportUrl} from 'common/report/util';
import {SharingAccessType} from 'common/widgets/sharing/types';
import {SharingModalDisabledReason} from 'common/widgets/sharing/modal/types';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {ProjectDetails} from 'common/report/stores/project-details';
import {getLocation} from 'common/util/url';

interface ValidateSharingAttributesOptions {
  ignoreEntityId?: boolean;
}

export const sharingUtils = {
  validateSharingAttributes,
  getEntityUrl,
  createShortUrl,
  createPublicDashboardLink,
  getSharingModalDisabledReasonKey,
};

function validateSharingAttributes({
  projectId,
  id,
  type,
  options = {
    ignoreEntityId: false,
  },
}: {
  projectId: number;
  id: number;
  type: SharableEntityType;
  options?: ValidateSharingAttributesOptions;
}) {
  return Boolean(projectId && (id || options.ignoreEntityId) && Object.values(SharableEntityType).includes(type));
}

// Generate shareable entity url
function getEntityUrl({
  entityLink,
  projectId,
  workspaceId,
}: {
  entityLink: string;
  projectId: number;
  workspaceId: string;
}) {
  return entityLink ? getLocation().origin + getReportUrl(entityLink, {projectId, workspaceId}) : ``;
}

/**
 * returns a short url for a current url in the address bar
 * used in: dropdown share button and share button in report page (non-list version)
 *
 * The string returned is copied into the user's clipboard
 */
async function createShortUrl(targetUrl: string, location = getLocation()) {
  const shortUrlFetch = await fetch(`/s/`, {
    credentials: `include`,
    method: `POST`,
    body: targetUrl,
  });

  const shortCodeResp = await shortUrlFetch.text();
  if (shortUrlFetch.status !== 200) {
    throw new Error(shortCodeResp);
  } else {
    return `${location.protocol}//${location.hostname}/s/${shortCodeResp}`;
  }
}

/**
 * This returns a string that is copied into the user's clipboard for a public dashboard.
 */
function createPublicDashboardLink(publicLinkId: string, location = getLocation()) {
  // warm screenshot image cache for any known clients to preview, we don't need the response here
  fetch(`/social/dashboards/${publicLinkId}`);
  return `${location.protocol}//${location.hostname}/p/${publicLinkId}`;
}

function getSharingModalDisabledReasonKey(sharingAccessType: SharingAccessType) {
  switch (sharingAccessType) {
    case SharingAccessType.Viewer:
      return SharingModalDisabledReason.Viewer;
    case SharingAccessType.None:
      return SharingModalDisabledReason.Consumer;
    default:
      return SharingModalDisabledReason.Default;
  }
}

export function getProjectRoleName(project: CurrentProject | ProjectDetails | null): string | null {
  return project?.role?.name || UserRole.Consumer;
}
